import { parsePeerId, playerIdFromPeerId } from '../security/input.js';
import { getPlayerProfile } from './playerProfiles.js';

/** Soft cap for the recently-active lobby directory shown in Online now. */
export const LOBBY_RECENT_ACTIVE_LIMIT = 100;

/** Concurrent WebRTC mesh targets sampled from the recent-active directory. */
export const LOBBY_MESH_FANOUT = 24;

export const LOBBY_DIRECTORY_MESSAGE_TYPE = 'dechess-lobby-directory';
/** Dial known lobby peers often enough that Online now does not wait on the basket. */
export const LOBBY_MESH_INTERVAL_MS = 5_000;
/** WebRTC directory gossip — nicknames for the challenge list. Not PeerBasket. */
export const LOBBY_DIRECTORY_BROADCAST_MS = 3_000;
/** Steady PeerBasket poll. A shorter warmup runs once after join. */
export const LOBBY_RENDEZVOUS_INTERVAL_MS = 20_000;
/** One extra inbox poll after join, at the hard PeerBasket gap, so strangers show up sooner. */
export const LOBBY_PRESENCE_WARMUP_MS = 10_000;
/** Rediscover unlinked lobby peers without re-announcing on every presence tick. */
export const LOBBY_RELINK_INTERVAL_MS = 12_000;
export const LOBBY_RECENT_TTL_MS = 10 * 60 * 1000;

function shuffle(list, randomFn = Math.random) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(randomFn() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function mergeLobbyEntries(base = null, next) {
  if (!base) {
    return next;
  }
  return {
    ...base,
    ...next,
    nickname: next.nickname || base.nickname || '',
    country: next.country || base.country || '',
    playerId: next.playerId || base.playerId || null,
    openToChallenges: next.openToChallenges,
    archivist: next.archivist,
    certShard: next.certShard || base.certShard || '',
    lastSeenAt: Math.max(base.lastSeenAt || 0, next.lastSeenAt || 0),
    peerId: (next.lastSeenAt || 0) >= (base.lastSeenAt || 0) ? next.peerId : base.peerId
  };
}

export function enrichLobbyPeerEntry(entry) {
  if (!entry) {
    return null;
  }
  const profile = entry.playerId ? getPlayerProfile(entry.playerId) : null;
  return {
    ...entry,
    nickname: entry.nickname || profile?.nickname || '',
    country: entry.country || profile?.country || ''
  };
}

export function normalizeLobbyPeerEntry(entry, now = Date.now()) {
  if (!entry) {
    return null;
  }

  const peerId = parsePeerId(entry.peerId || entry.senderId || entry.id);
  if (!peerId) {
    return null;
  }

  const playerId = entry.playerId
    || entry.metadata?.playerId
    || playerIdFromPeerId(peerId);
  const lastSeenAt = Number(
    entry.lastSeenAt
    || entry.metadata?.lastSeenAt
    || Date.parse(entry.sentAt || '')
    || now
  );

  return enrichLobbyPeerEntry({
    peerId,
    playerId: playerId || null,
    nickname: entry.nickname || entry.metadata?.nickname || '',
    country: entry.country || entry.metadata?.country || '',
    openToChallenges: entry.openToChallenges === undefined
      && entry.metadata?.openToChallenges === undefined
      ? true
      : Boolean(
        entry.openToChallenges ?? entry.metadata?.openToChallenges
      ),
    archivist: Boolean(entry.archivist ?? entry.metadata?.archivist),
    certShard: entry.certShard || entry.metadata?.certShard || '',
    lastSeenAt: Number.isFinite(lastSeenAt) ? lastSeenAt : now
  });
}

export function upsertRecentLobbyPeers(registry = [], entries = [], {
  now = Date.now(),
  limit = LOBBY_RECENT_ACTIVE_LIMIT,
  ttlMs = LOBBY_RECENT_TTL_MS,
  excludePeerIds = [],
  excludePlayerIds = []
} = {}) {
  const excludedPeers = new Set((excludePeerIds || []).filter(Boolean));
  const excludedPlayers = new Set(
    (excludePlayerIds || []).filter(Boolean).map((id) => String(id).toLowerCase())
  );
  const byPeerId = new Map();

  for (const existing of registry) {
    const normalized = normalizeLobbyPeerEntry(existing, now);
    if (!normalized || excludedPeers.has(normalized.peerId)) {
      continue;
    }
    if (
      normalized.playerId
      && excludedPlayers.has(String(normalized.playerId).toLowerCase())
    ) {
      continue;
    }
    if (now - normalized.lastSeenAt > ttlMs) {
      continue;
    }
    byPeerId.set(normalized.peerId, normalized);
  }

  for (const entry of entries) {
    const normalized = normalizeLobbyPeerEntry(entry, now);
    if (!normalized || excludedPeers.has(normalized.peerId)) {
      continue;
    }
    if (
      normalized.playerId
      && excludedPlayers.has(String(normalized.playerId).toLowerCase())
    ) {
      continue;
    }
    const previous = byPeerId.get(normalized.peerId);
    if (!previous || normalized.lastSeenAt >= previous.lastSeenAt) {
      byPeerId.set(normalized.peerId, mergeLobbyEntries(previous, normalized));
    } else {
      byPeerId.set(normalized.peerId, mergeLobbyEntries(normalized, previous));
    }
  }

  const byPlayerId = new Map();
  for (const entry of byPeerId.values()) {
    const key = String(entry.playerId || entry.peerId).toLowerCase();
    byPlayerId.set(key, mergeLobbyEntries(byPlayerId.get(key), entry));
  }

  return [...byPlayerId.values()]
    .sort((left, right) => right.lastSeenAt - left.lastSeenAt)
    .slice(0, Math.max(0, limit));
}

export function selectRandomMeshTargets(registry = [], {
  count = LOBBY_MESH_FANOUT,
  excludePeerIds = [],
  preferPeerIds = [],
  randomFn = Math.random
} = {}) {
  const excluded = new Set((excludePeerIds || []).filter(Boolean));
  const preferred = new Set((preferPeerIds || []).filter(Boolean));
  const entries = registry
    .map((entry) => (
      typeof entry === 'string'
        ? { peerId: entry, lastSeenAt: 0 }
        : entry
    ))
    .filter((entry) => entry?.peerId && !excluded.has(entry.peerId));

  // Freshest directory rows first so Online now peers get live links quickly.
  const byFreshness = [...entries].sort(
    (left, right) => (right.lastSeenAt || 0) - (left.lastSeenAt || 0)
  );
  const preferredPeers = byFreshness
    .filter((entry) => preferred.has(entry.peerId))
    .map((entry) => entry.peerId);
  const others = byFreshness
    .filter((entry) => !preferred.has(entry.peerId))
    .map((entry) => entry.peerId);

  return [
    ...preferredPeers,
    ...shuffle(others, randomFn)
  ].filter((peerId, index, list) => list.indexOf(peerId) === index)
    .slice(0, Math.max(0, count));
}

export function selectFreshDialPeerIds(registry = [], {
  selfPeerId = '',
  limit = 8,
  maxAgeMs = 45_000,
  now = Date.now()
} = {}) {
  const byPlayer = new Map();
  for (const entry of registry || []) {
    const peerId = entry?.peerId;
    if (!peerId || peerId === selfPeerId) {
      continue;
    }
    if (maxAgeMs > 0 && now - (entry.lastSeenAt || 0) > maxAgeMs) {
      continue;
    }
    const key = String(entry.playerId || peerId).toLowerCase();
    const previous = byPlayer.get(key);
    if (!previous || (entry.lastSeenAt || 0) >= (previous.lastSeenAt || 0)) {
      byPlayer.set(key, entry);
    }
  }
  return [...byPlayer.values()]
    .sort((left, right) => (right.lastSeenAt || 0) - (left.lastSeenAt || 0))
    .map((entry) => entry.peerId)
    .filter((peerId, index, list) => peerId && list.indexOf(peerId) === index)
    .slice(0, Math.max(0, limit));
}

/** All known PeerJS session ids for a player (freshest first). */
export function peerIdsForPlayer(registry = [], playerId = '') {
  const normalized = String(playerId || '').toLowerCase();
  if (!normalized) {
    return [];
  }
  return [...registry]
    .filter((entry) => String(entry?.playerId || '').toLowerCase() === normalized)
    .sort((left, right) => (right.lastSeenAt || 0) - (left.lastSeenAt || 0))
    .map((entry) => entry.peerId)
    .filter((peerId, index, list) => peerId && list.indexOf(peerId) === index);
}

export function lobbyPeersFromRoom(remotePeers = [], now = Date.now()) {
  return remotePeers.map((peer) => normalizeLobbyPeerEntry({
    peerId: peer.peerId,
    metadata: peer.metadata,
    lastSeenAt: peer.lastSeenAt || now
  }, now)).filter(Boolean);
}

export function lobbyPeersFromHellos(messages = [], now = Date.now()) {
  return messages.map((message) => normalizeLobbyPeerEntry({
    peerId: message.senderId,
    playerId: message.payload?.playerId,
    nickname: message.payload?.nickname,
    country: message.payload?.country,
    openToChallenges: message.payload?.openToChallenges,
    archivist: message.payload?.archivist,
    certShard: message.payload?.certShard,
    sentAt: message.payload?.sentAt,
    lastSeenAt: Date.parse(message.payload?.sentAt || '') || now
  }, now)).filter(Boolean);
}

export function lobbyPeersFromDirectoryPayload(payload = {}, now = Date.now()) {
  const peers = Array.isArray(payload.peers) ? payload.peers : [];
  return peers
    .map((entry) => normalizeLobbyPeerEntry(entry, now))
    .filter(Boolean);
}

export function buildLobbyDirectoryPayload(registry = [], {
  selfPeerId = '',
  selfPlayerId = '',
  nickname = '',
  country = '',
  openToChallenges = true,
  archivist = false,
  certShard = '',
  now = Date.now(),
  limit = LOBBY_RECENT_ACTIVE_LIMIT,
  rooms = [],
  recentChat = []
} = {}) {
  const selfEntry = selfPeerId
    ? normalizeLobbyPeerEntry({
      peerId: selfPeerId,
      playerId: selfPlayerId,
      nickname,
      country,
      openToChallenges,
      archivist,
      certShard,
      lastSeenAt: now
    }, now)
    : null;

  const peers = upsertRecentLobbyPeers(
    selfEntry ? [selfEntry, ...registry] : registry,
    [],
    { now, limit, excludePeerIds: [], excludePlayerIds: [] }
  ).map((entry) => ({
    peerId: entry.peerId,
    playerId: entry.playerId,
    nickname: entry.nickname,
    country: entry.country,
    openToChallenges: entry.openToChallenges,
    archivist: Boolean(entry.archivist),
    certShard: entry.certShard || '',
    lastSeenAt: entry.lastSeenAt
  }));

  const syncRooms = (Array.isArray(rooms) ? rooms : [])
    .filter((room) => room?.roomId && room.roomId !== 'main')
    .slice(-12)
    .map((room) => ({
      roomId: String(room.roomId).slice(0, 32),
      title: String(room.title || room.roomId).slice(0, 32),
      createdBy: room.createdBy || '',
      createdAt: Number(room.createdAt) || now
    }));

  const syncChat = (Array.isArray(recentChat) ? recentChat : [])
    .slice(-12)
    .map((entry) => ({
      roomId: String(entry.roomId || 'main').slice(0, 32),
      text: String(entry.text || '').slice(0, 160),
      nickname: String(entry.nickname || '').slice(0, 32),
      playerId: entry.playerId || '',
      senderId: entry.senderId || '',
      sentAt: Number(entry.sentAt) || now
    }))
    .filter((entry) => entry.text && entry.playerId);

  return {
    sentAt: new Date(now).toISOString(),
    peers,
    rooms: syncRooms,
    recentChat: syncChat
  };
}

export function recentPeersAsRoomPeers(registry = []) {
  return registry.map((entry) => {
    const enriched = enrichLobbyPeerEntry(entry) || entry;
    return {
      peerId: enriched.peerId,
      metadata: {
        playerId: enriched.playerId,
        nickname: enriched.nickname,
        country: enriched.country,
        openToChallenges: enriched.openToChallenges,
        archivist: Boolean(enriched.archivist),
        certShard: enriched.certShard || ''
      },
      lastSeenAt: enriched.lastSeenAt
    };
  });
}

export function lobbyRegistryFingerprint(registry = []) {
  // Exclude lastSeenAt — presence heartbeats must not thrash React state or
  // re-trigger hello/directory broadcasts (that loop floods the lobby).
  return registry
    .map((entry) => [
      entry.peerId,
      entry.playerId || '',
      entry.nickname || '',
      entry.country || '',
      entry.openToChallenges ? '1' : '0',
      entry.archivist ? '1' : '0',
      entry.certShard || ''
    ].join(':'))
    .join('|');
}
